<template>
  <dropdown
    v-bind="dropdownProps"
    v-model="open">
    <a
      role="combobox"
      :aria-controls="dropdownMenuId"
      v-bind="buttonProps"
      @keydown="handleDropdownKeydown"
      @focus="$emit('focusedDropdownButton')"
      @click="$emit('openedDropdown', open)"
      :aria-expanded="open ? 'true' : 'false'"
      tabindex="0">
      <slot name="button-contents" />
    </a>
    <tooltip
      v-if="buttonProps && buttonProps.tooltipText && buttonProps.id"
      :target="'#' + buttonProps.id"
      :text="buttonProps.tooltipText"
      placement="top"
      :enterable="true"
      @show="$emit('tooltipShow')"
      @hide="$emit('tooltipHide')" />
    <template slot="dropdown">
      <slot name="dropdown-items" />
    </template>
  </dropdown>
</template>

<script>
export default {
  name: 'A11yDropdown',
  data() {
    return {
      open: false,
      // True while a pointer press that started inside the dropdown is in progress,
      // so a null-relatedTarget focusout (an hrefless option click) doesn't close the
      // menu before the click selects. See handleFocusOut.
      pointerDownInside: false
    }
  },
  props: {
    dropdownProps: {type: Object, default: null},
    buttonProps: {type: Object, default: null},
    domAttributes: {type: Object, default: null},
    restoreFocus: {type: Boolean, default: false}
  },
  computed: {
    // The trigger's role="combobox" always needs aria-controls to reference the
    // menu's real id, so fall back to a generated one when a consumer doesn't
    // supply domAttributes.id (this is what was missing on several dropdowns).
    dropdownMenuId() {
      return (this.domAttributes && this.domAttributes.id) || `a11y-dropdown-menu-${this._uid}`
    }
  },
  methods: {
    setDomAttributes() {
      // get the ref from the dropdownProps which refers to the uiv dropdown component which itself has a ref called dropdown which is the ul for the dropdown menu
      const menu = this.$refs?.[this.dropdownProps?.ref]?.$refs?.dropdown
      if (!menu) {
        return
      }
      menu.setAttribute('id', this.dropdownMenuId)
      if (this.domAttributes) {
        for (const [key, val] of Object.entries(this.domAttributes)) {
          if (key === 'id') {
            continue
          }
          menu.setAttribute(key, val)
        }
      }
    },
    handleDropdownKeydown(event) {
      const keys = ['Enter', ' ', 'ArrowDown']
      if (keys.includes(event.key)) {
        event.preventDefault()
        this.handleOpenDropdown(event.key)
      }
    },
    handleOpenDropdown(key) {
      if (!this.open || key !== 'ArrowDown') {
        this.$emit('openedDropdown', open)
        this.$refs[this.dropdownProps.ref].toggle()
      }
    },
    handleFocusOut(event) {
      // uiv's Dropdown only closes on Escape or an outside click/touch — never when
      // keyboard focus tabs away, so a still-open menu stays painted on top of
      // whatever the user just tabbed to (WCAG 2.4.11 focus-not-obscured).
      if (!this.open) {
        return
      }
      const nextFocus = event.relatedTarget
      if (nextFocus) {
        // Focus moved to a real element: close only if it's outside the dropdown
        // (the tab-away case); stay open when it moved within (trigger -> item).
        if (!this.$el.contains(nextFocus)) {
          this.open = false
        }
        return
      }
      // Null relatedTarget is ambiguous: it happens both when a pointer presses an
      // hrefless `<a role="option">` item (the item takes no focus) AND when keyboard
      // focus leaves the page entirely. Stay open only for the former — a pointer
      // press in progress inside the dropdown — so the click can still select;
      // otherwise close so the menu can't obscure content after focus leaves.
      if (!this.pointerDownInside) {
        this.open = false
      }
    },
    markPointerDownInside(event) {
      this.pointerDownInside = this.$el.contains(event.target)
    },
    clearPointerDown() {
      this.pointerDownInside = false
    }
  },
  mounted() {
    this.setDomAttributes()
    this.$el.addEventListener('focusout', this.handleFocusOut)
    this.$el.addEventListener('mousedown', this.markPointerDownInside)
    this.$el.addEventListener('touchstart', this.markPointerDownInside)
    window.addEventListener('mouseup', this.clearPointerDown)
    window.addEventListener('touchend', this.clearPointerDown)
    window.addEventListener('touchcancel', this.clearPointerDown)
    window.addEventListener('blur', this.clearPointerDown)
  },
  beforeDestroy() {
    this.$el.removeEventListener('focusout', this.handleFocusOut)
    this.$el.removeEventListener('mousedown', this.markPointerDownInside)
    this.$el.removeEventListener('touchstart', this.markPointerDownInside)
    window.removeEventListener('mouseup', this.clearPointerDown)
    window.removeEventListener('touchend', this.clearPointerDown)
    window.removeEventListener('touchcancel', this.clearPointerDown)
    window.removeEventListener('blur', this.clearPointerDown)
  },
  watch: {
    domAttributes: {
      deep: true,
      handler() {
        this.setDomAttributes()
      }
    },
    open(isOpen, wasOpen) {
      if (!this.restoreFocus || !wasOpen || isOpen) {
        return
      }
      this.$nextTick(() => {
        const uivDropdown = this.$refs?.[this.dropdownProps?.ref]
        const trigger = uivDropdown && uivDropdown.triggerEl
        if (!trigger) {
          return
        }
        const active = document.activeElement
        const menu = uivDropdown.$refs?.dropdown
        const focusLost = !active || active === document.body
        const focusInMenu = menu ? menu.contains(active) : false
        if (focusLost || focusInMenu) {
          trigger.focus()
        }
      })
    }
  }
}
</script>
